import { Expression } from 'mapbox-gl'

import { DroverEvents, Polygon, Trip_LocationTypesEnum } from 'src/api/gen'
import { DEFAULT_MAIN_COLOR } from 'src/components/ColorDot'
import { LOCATION_TYPES_COLOR } from 'src/theme'

import { IClickedDroverPoint } from './DroverPopup'
import { SOURCE_NAME } from './utils'

export function droversToGeoJSON(
  drovers: DroverEvents[],
  droversToProperties: (o: DroverEvents) => { [key: string]: any } = (o) => o
) {
  const features = drovers.map((drover: DroverEvents) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [drover.lon, drover.lat],
    },
    properties: droversToProperties(drover),
  }))

  return {
    type: 'FeatureCollection',
    features,
  }
}

export async function addDroversToMap(
  map: mapboxgl.Map,
  assets: DroverEvents[],
  setClickedDroverPoint: (clickedDroverPoint: IClickedDroverPoint | undefined) => void
) {
  map.addSource(SOURCE_NAME, {
    type: 'geojson',
    data: droversToGeoJSON(assets),
  } as mapboxgl.AnySourceData)

  const circleColorMapping = Object.values(Trip_LocationTypesEnum).map((location_type) => [
    location_type,
    LOCATION_TYPES_COLOR[location_type] ?? DEFAULT_MAIN_COLOR,
  ])

  map.addLayer({
    id: 'drover-events-points',
    type: 'circle',
    source: SOURCE_NAME,
    paint: {
      'circle-radius': {
        base: 2,
        stops: [
          [1, 1],
          [14, 6],
          [15, 7],
          [17, 14],
        ],
      },
      'circle-color': [
        'match',
        ['get', 'location_type'],
        ...Array.prototype.concat(...circleColorMapping),
        /* other */ '#ccc',
      ] as Expression,
    },
  })

  const displayDroverPopup = () => async (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
    const clickedPoint = map
      ? map.queryRenderedFeatures(event.point, {
          layers: ['drover-events-points'],
        })
      : []
    if (clickedPoint.length && clickedPoint[0].properties) {
      setClickedDroverPoint({
        coordinates: event.lngLat,
        drover: clickedPoint[0].properties as DroverEvents,
      })
    }
  }

  map.on('click', displayDroverPopup())
}

export function polygonsToGeoJSON(polygons: Polygon[]) {
  const features = polygons.map((polygon: Polygon) => ({
    type: 'Feature',
    geometry: polygon,
  }))

  return {
    type: 'FeatureCollection',
    features: features,
  }
}

export async function addMapLayersToMap(map: mapboxgl.Map, assets: Polygon[]) {
  map.addSource('map-layers-source', {
    type: 'geojson',
    data: polygonsToGeoJSON(assets),
  } as mapboxgl.AnySourceData)

  map.addLayer({
    id: 'map-layers',
    type: 'fill',
    source: 'map-layers-source',
    layout: {},
    paint: {
      'fill-color': '#000',
      'fill-opacity': 0.05,
    },
  })
}
